import React from "react";
import {
  Button,
  SvgIcon,
  Tab,
  styled,
  useMediaQuery,
  useTheme,
  ButtonBase,
  Chip,
} from "@mui/material";
import { TabList } from "@mui/lab";

export const PrimaryButton = ({ children, sx, ...props }) => {
  const defaultSx = {
    borderRadius: "8px",
    padding: "8px 20px",
    height: "40px",
    fontSize: (theme) => theme.typography.body1.fontSize,
    textTransform: "none",
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#F53D3B" : "#F53D3B",
    color: (theme) => (theme.palette.mode === "light" ? "#FFFFFF" : "#FFFFFF"),
    "&:hover": {
      backgroundColor: (theme) =>
        theme.palette.mode === "light" ? "#D63533" : "#D63533",
    },
    "&.Mui-disabled": {
      backgroundColor: "#B22D2C",
      color: "rgba(255, 255, 255, 0.7)",
    },
  };

  // Merge the default styles with any additional sx prop passed
  const mergedSx = (theme) => ({
    ...defaultSx,
    ...(typeof sx === "function" ? sx(theme) : sx),
  });

  return (
    <Button variant="contained" disableElevation sx={mergedSx} {...props}>
      {children}
    </Button>
  );
};

export const OutlinedPrimaryButton = ({ children, sx, ...props }) => {
  const defaultSx = {
    borderRadius: "8px",
    padding: "8px 20px",
    height: "40px",
    fontSize: (theme) => theme.typography.body1.fontSize,
    textTransform: "none",
    border: "1px solid #E9DDB3",
    color: (theme) => (theme.palette.mode === "light" ? "#000000" : "#000000"),
    "&:hover": {
      backgroundColor: "rgba(233, 221, 179, 0.1)", // Light version of #E9DDB3
      border: "1px solid #d6cb9f",
    },
  };

  // Merge the default styles with any additional sx prop passed
  const mergedSx = (theme) => ({
    ...defaultSx,
    ...(typeof sx === "function" ? sx(theme) : sx),
  });

  return (
    <Button variant="outlined" disableElevation sx={mergedSx} {...props}>
      {children}
    </Button>
  );
};

export const ResponsiveChip = ({ technique, isSelected, onSelect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <ButtonBase
      onClick={() => onSelect(technique.name)}
      sx={{
        borderRadius: "16px",
        border: "1px solid #e0e0e0",
        backgroundColor: isSelected ? "#ff69b4" : "white",
        color: isSelected ? "white" : "black",
        "&:active": {
          backgroundColor: "#ff69b4",
          color: "white",
        },
        "&:hover": {
          backgroundColor: isMobile
            ? isSelected
              ? "#ff69b4"
              : "white"
            : isSelected
            ? "#ff69b4"
            : "#f0f0f0",
        },
        padding: "0 12px",
        height: "auto",
        minHeight: "32px",
        margin: "2px",
        transition: "none",
        fontSize: "14px",
        fontWeight: 400,
        fontFamily: "Instrument Sans, sans-serif",
      }}
    >
      {`${technique.name} (${technique.difficulty}/10)`}
    </ButtonBase>
  );
};

export const StarSVG1 = ({ isMobile }) => (
  <svg
    width="42"
    height="37"
    viewBox="0 0 42 37"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={isMobile ? { marginLeft: "12px" } : {}}
  >
    <path
      d="M42 15.7371L38.022 9.25158L28.9593 14.1773V0H21.0034V9.85151L7.96262 2.75968L3.97795 9.25158L13.0407 14.1773L0 21.2628L3.97795 27.7484L13.0407 22.829V37H21.0034V27.1548L34.0441 34.2403L38.022 27.7484L28.9593 22.829L42 15.7371Z"
      fill="#FF72B3"
    />
  </svg>
);

export const StarSVG2 = ({ isMobile }) => (
  <svg
    width="69"
    height="66"
    viewBox="0 0 69 66"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={isMobile ? { marginRight: "12px" } : {}}
  >
    <path
      d="M48.6019 34.0478C41.4721 34.352 35.7615 40.0642 35.4687 47.1847L34.5 66L33.5313 47.1847C33.2272 40.0529 27.5166 34.3407 20.3981 34.0478L0 33L20.3981 31.9522C27.5279 31.648 33.2385 25.9358 33.5313 18.8153L34.5 0L35.4687 18.8153C35.7728 25.9471 41.4834 31.6593 48.6019 31.9522L69 33L48.6019 34.0478Z"
      fill="#17B082"
    />
  </svg>
);

export const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "16px",
  marginRight: "10px",
  color: "#333",
  borderRadius: "12px 12px 0 0",
  height: "40px",
  "&.Mui-selected": {
    backgroundColor: "#F5F5DC",
    color: "#333",
  },
}));

export const StyledTabList = styled(TabList)(({ theme }) => ({
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));

export const blurredBoxStyle = {
  bgcolor: "rgba(255, 255, 255, 0.5)",
  backdropFilter: "blur(16px)",
  borderRadius: 2,
  p: 4,
};

export const sectionStyle = {
  mx: 6,
  mb: 3,
};

export const mobileBlurredBoxStyle = {
  ...blurredBoxStyle,
  p: 2,
};

export const mobileSectionStyle = {
  ...sectionStyle,
  mx: 0,
};

export const innerBoxStyle = {
  bgcolor: "white",
  borderRadius: 2,
  p: 2,
  height: "100%",
};

export const mobileInnerBoxStyle = {
  ...innerBoxStyle,
  mx: 2, // Add horizontal margin for mobile
};

export const bannerStyle = {
  fontFamily: "Unbounded, sans-serif",
  color: "#CFC192",
  fontWeight: 400,
  fontSize: "20px",
};

export const titleStyle = {
  fontFamily: "Unbounded, sans-serif",
  fontWeight: "400",
  fontSize: "20px",
};

export const typographyStyle = {
  fontFamily: "Instrument Sans, sans-serif",
  lineHeight: "24px",
  color: "#605943",
  fontWeight: 400,
  fontSize: "18px",
};

export const headerStyle = {
  fontFamily: "Unbounded, sans-serif",
  fontWeight: "400",
  fontSize: "18px",
  color: "#605943",
};

export const VoiceIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#605943"
      fill="none"
    >
      <path
        d="M2.5 12C2.5 7.52166 2.5 5.28249 3.89124 3.89124C5.28249 2.5 7.52166 2.5 12 2.5C16.4783 2.5 18.7175 2.5 20.1088 3.89124C21.5 5.28249 21.5 7.52166 21.5 12C21.5 16.4783 21.5 18.7175 20.1088 20.1088C18.7175 21.5 16.4783 21.5 12 21.5C7.52166 21.5 5.28249 21.5 3.89124 20.1088C2.5 18.7175 2.5 16.4783 2.5 12Z"
        stroke="currentColor"
        stroke-width="1.5"
      />
      <path
        d="M12 8V16"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M9 10V14"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6 11V13"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M15 10V14"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M18 11V13"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const StarIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#605943"
      fill="none"
    >
      <path
        d="M13.7276 3.44418L15.4874 6.99288C15.7274 7.48687 16.3673 7.9607 16.9073 8.05143L20.0969 8.58575C22.1367 8.92853 22.6167 10.4206 21.1468 11.8925L18.6671 14.3927C18.2471 14.8161 18.0172 15.6327 18.1471 16.2175L18.8571 19.3125C19.417 21.7623 18.1271 22.71 15.9774 21.4296L12.9877 19.6452C12.4478 19.3226 11.5579 19.3226 11.0079 19.6452L8.01827 21.4296C5.8785 22.71 4.57865 21.7522 5.13859 19.3125L5.84851 16.2175C5.97849 15.6327 5.74852 14.8161 5.32856 14.3927L2.84884 11.8925C1.389 10.4206 1.85895 8.92853 3.89872 8.58575L7.08837 8.05143C7.61831 7.9607 8.25824 7.48687 8.49821 6.99288L10.258 3.44418C11.2179 1.51861 12.7777 1.51861 13.7276 3.44418Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const EditIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#605943"
      fill="none"
    >
      <path
        d="M14.0737 3.88545C14.8189 3.07808 15.1915 2.6744 15.5874 2.43893C16.5427 1.87076 17.7191 1.85309 18.6904 2.39232C19.0929 2.6158 19.4769 3.00812 20.245 3.79276C21.0131 4.5774 21.3972 4.96972 21.6159 5.38093C22.1438 6.37312 22.1265 7.57479 21.5703 8.5507C21.3398 8.95516 20.9446 9.33578 20.1543 10.097L10.7506 19.1543C9.25288 20.5969 8.504 21.3182 7.56806 21.6837C6.63212 22.0493 5.6032 22.0224 3.54536 21.9686L3.26538 21.9613C2.63891 21.9449 2.32567 21.9367 2.14359 21.73C1.9615 21.5234 1.98636 21.2043 2.03608 20.5662L2.06308 20.2197C2.20301 18.4235 2.27297 17.5255 2.62371 16.7182C2.97444 15.9109 3.57944 15.2555 4.78943 13.9445L14.0737 3.88545Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <path
        d="M13 4L20 11"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <path
        d="M14 22L22 22"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const XPIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      width="20"
      height="22"
      viewBox="0 0 20 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.8655 11L19.6583 6.32812C19.7244 6.21838 19.7591 6.09384 19.7588 5.96712C19.7585 5.84041 19.7233 5.71602 19.6566 5.60656C19.59 5.4971 19.4944 5.40647 19.3794 5.34385C19.2645 5.28124 19.1343 5.24886 19.0021 5.25H13.438L10.6583 0.57812C10.5928 0.467933 10.498 0.37633 10.3835 0.312612C10.2691 0.248895 10.1391 0.215332 10.0067 0.215332C9.8744 0.215332 9.74441 0.248895 9.62997 0.312612C9.51554 0.37633 9.42073 0.467933 9.35519 0.57812L6.56425 5.25H1.00206C0.870235 5.24965 0.740644 5.28261 0.626344 5.34555C0.512045 5.4085 0.417076 5.4992 0.351008 5.60852C0.28494 5.71784 0.250106 5.84192 0.250016 5.96826C0.249927 6.09459 0.284585 6.21871 0.350498 6.32812L3.13675 11L0.350498 15.6719C0.284379 15.7816 0.249709 15.9061 0.250002 16.0329C0.250295 16.1596 0.28554 16.284 0.352166 16.3934C0.418792 16.5029 0.514429 16.5935 0.629384 16.6561C0.744339 16.7188 0.874526 16.7511 1.00675 16.75H6.56425L9.3505 21.4219C9.41604 21.5321 9.51085 21.6237 9.62528 21.6874C9.73972 21.7511 9.86971 21.7847 10.0021 21.7847C10.1344 21.7847 10.2644 21.7511 10.3788 21.6874C10.4933 21.6237 10.5881 21.5321 10.6536 21.4219L13.438 16.75H19.0021C19.1343 16.7511 19.2645 16.7188 19.3794 16.6561C19.4944 16.5935 19.59 16.5029 19.6566 16.3934C19.7233 16.284 19.7585 16.1596 19.7588 16.0329C19.7591 15.9061 19.7244 15.7816 19.6583 15.6719L16.8655 11ZM15.1386 11L12.568 15.3125H7.43519L4.86456 11L7.43519 6.6875H12.568L15.1386 11Z"
        fill="#17B082"
      />
    </svg>
  </SvgIcon>
);

export const LevelIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      width="24"
      height="22"
      viewBox="0 0 24 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M24 9.43189L21.7269 5.75089L16.5482 8.5466V0.5H12.0019V6.0914L4.55007 2.06631L2.27312 5.75089L7.45185 8.5466L0 12.5681L2.27312 16.2491L7.45185 13.457V21.5H12.0019V15.9122L19.4538 19.9337L21.7269 16.2491L16.5482 13.457L24 9.43189Z"
        fill="#FF72B3"
      />
    </svg>
  </SvgIcon>
);

export const RedoIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#ff72b3"
      fill="none"
    >
      <path
        d="M16.3884 3L17.3913 3.97574C17.8393 4.41165 18.0633 4.62961 17.9844 4.81481C17.9056 5 17.5888 5 16.9552 5H9.19422C5.22096 5 2 8.13401 2 12C2 13.4872 2.47668 14.8662 3.2895 16"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M7.61156 21L6.60875 20.0243C6.16074 19.5883 5.93673 19.3704 6.01557 19.1852C6.09441 19 6.4112 19 7.04478 19H14.8058C18.779 19 22 15.866 22 12C22 10.5128 21.5233 9.13383 20.7105 8"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const ReportIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#ff72b3"
      fill="none"
    >
      <path
        d="M6.5 17.5L6.5 14.5M11.5 17.5L11.5 8.5M16.5 17.5V13.5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      />
      <path
        d="M21.5 5.5C21.5 7.15685 20.1569 8.5 18.5 8.5C16.8431 8.5 15.5 7.15685 15.5 5.5C15.5 3.84315 16.8431 2.5 18.5 2.5C20.1569 2.5 21.5 3.84315 21.5 5.5Z"
        stroke="currentColor"
        stroke-width="1.5"
      />
      <path
        d="M21.4955 11C21.4955 11 21.5 11.3395 21.5 12C21.5 16.4784 21.5 18.7175 20.1088 20.1088C18.7175 21.5 16.4783 21.5 12 21.5C7.52166 21.5 5.28249 21.5 3.89124 20.1088C2.5 18.7175 2.5 16.4784 2.5 12C2.5 7.52169 2.5 5.28252 3.89124 3.89127C5.28249 2.50003 7.52166 2.50003 12 2.50003L13 2.5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const YellowStarIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.405 12.381C14.9251 12.4916 12.9388 14.5688 12.8369 17.1581L12.5 24L12.1631 17.1581C12.0573 14.5647 10.071 12.4875 7.595 12.381L0.5 12L7.595 11.619C10.0749 11.5084 12.0612 9.4312 12.1631 6.84193L12.5 0L12.8369 6.84193C12.9427 9.4353 14.929 11.5125 17.405 11.619L24.5 12L17.405 12.381Z"
        fill="#FFC249"
      />
    </svg>
  </SvgIcon>
);

export const WandIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#605943"
      fill="none"
    >
      <path
        d="M13.9258 12.7775L11.7775 10.6292C11.4847 10.3364 11.3383 10.19 11.1803 10.1117C10.8798 9.96277 10.527 9.96277 10.2264 10.1117C10.0685 10.19 9.92207 10.3364 9.62923 10.6292C9.33638 10.9221 9.18996 11.0685 9.11169 11.2264C8.96277 11.527 8.96277 11.8798 9.11169 12.1803C9.18996 12.3383 9.33638 12.4847 9.62923 12.7775L11.7775 14.9258M13.9258 12.7775L20.3708 19.2225C20.6636 19.5153 20.81 19.6617 20.8883 19.8197C21.0372 20.1202 21.0372 20.473 20.8883 20.7736C20.81 20.9315 20.6636 21.0779 20.3708 21.3708C20.0779 21.6636 19.9315 21.81 19.7736 21.8883C19.473 22.0372 19.1202 22.0372 18.8197 21.8883C18.6617 21.81 18.5153 21.6636 18.2225 21.3708L11.7775 14.9258M13.9258 12.7775L11.7775 14.9258"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M17 2L17.2948 2.7966C17.6813 3.84117 17.8746 4.36345 18.2556 4.74445C18.6366 5.12545 19.1588 5.31871 20.2034 5.70523L21 6L20.2034 6.29477C19.1588 6.68129 18.6366 6.87456 18.2556 7.25555C17.8746 7.63655 17.6813 8.15883 17.2948 9.2034L17 10L16.7052 9.2034C16.3187 8.15884 16.1254 7.63655 15.7444 7.25555C15.3634 6.87455 14.8412 6.68129 13.7966 6.29477L13 6L13.7966 5.70523C14.8412 5.31871 15.3634 5.12545 15.7444 4.74445C16.1254 4.36345 16.3187 3.84117 16.7052 2.7966L17 2Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <path
        d="M6 4L6.22108 4.59745C6.51097 5.38087 6.65592 5.77259 6.94167 6.05834C7.22741 6.34408 7.61913 6.48903 8.40255 6.77892L9 7L8.40255 7.22108C7.61913 7.51097 7.22741 7.65592 6.94166 7.94167C6.65592 8.22741 6.51097 8.61913 6.22108 9.40255L6 10L5.77892 9.40255C5.48903 8.61913 5.34408 8.22741 5.05833 7.94167C4.77259 7.65592 4.38087 7.51097 3.59745 7.22108L3 7L3.59745 6.77892C4.38087 6.48903 4.77259 6.34408 5.05833 6.05833C5.34408 5.77259 5.48903 5.38087 5.77892 4.59745L6 4Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const SpeechIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#605943"
      fill="none"
    >
      <path
        d="M17 7V11C17 13.7614 14.7614 16 12 16C9.23858 16 7 13.7614 7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7Z"
        stroke="currentColor"
        stroke-width="1.5"
      />
      <path
        d="M20 11C20 15.4183 16.4183 19 12 19M12 19C7.58172 19 4 15.4183 4 11M12 19V22M12 22H15M12 22H9"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      />
    </svg>
  </SvgIcon>
);

export const PencilIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#605943"
      fill="none"
    >
      <path
        d="M6 22V13.6944C6 12.1287 6 11.3459 6.21454 10.6077C6.42908 9.86948 6.84589 9.21812 7.6795 7.91542L10.3359 3.76419C11.0885 2.58806 11.4648 2 12 2C12.5352 2 12.9115 2.58806 13.6641 3.76419L16.3205 7.91542C17.1541 9.21812 17.5709 9.86948 17.7855 10.6077C18 11.3459 18 12.1287 18 13.6944V22"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M7 11C7.63152 11.3231 8.4887 11.9732 9.28009 11.9991C10.2988 12.0324 10.9868 11.1372 12 11.1372C13.0132 11.1372 13.7012 12.0324 14.7199 11.9991C15.5113 11.9732 16.3685 11.3231 17 11"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M12 12L12 22"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M10 5H14"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const PurpleWandIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#7c4dff"
      fill="none"
    >
      <path
        d="M13.9258 12.7775L11.7775 10.6292C11.4847 10.3364 11.3383 10.19 11.1803 10.1117C10.8798 9.96277 10.527 9.96277 10.2264 10.1117C10.0685 10.19 9.92207 10.3364 9.62923 10.6292C9.33638 10.9221 9.18996 11.0685 9.11169 11.2264C8.96277 11.527 8.96277 11.8798 9.11169 12.1803C9.18996 12.3383 9.33638 12.4847 9.62923 12.7775L11.7775 14.9258M13.9258 12.7775L20.3708 19.2225C20.6636 19.5153 20.81 19.6617 20.8883 19.8197C21.0372 20.1202 21.0372 20.473 20.8883 20.7736C20.81 20.9315 20.6636 21.0779 20.3708 21.3708C20.0779 21.6636 19.9315 21.81 19.7736 21.8883C19.473 22.0372 19.1202 22.0372 18.8197 21.8883C18.6617 21.81 18.5153 21.6636 18.2225 21.3708L11.7775 14.9258M13.9258 12.7775L11.7775 14.9258"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M17 2L17.2948 2.7966C17.6813 3.84117 17.8746 4.36345 18.2556 4.74445C18.6366 5.12545 19.1588 5.31871 20.2034 5.70523L21 6L20.2034 6.29477C19.1588 6.68129 18.6366 6.87456 18.2556 7.25555C17.8746 7.63655 17.6813 8.15883 17.2948 9.2034L17 10L16.7052 9.2034C16.3187 8.15884 16.1254 7.63655 15.7444 7.25555C15.3634 6.87455 14.8412 6.68129 13.7966 6.29477L13 6L13.7966 5.70523C14.8412 5.31871 15.3634 5.12545 15.7444 4.74445C16.1254 4.36345 16.3187 3.84117 16.7052 2.7966L17 2Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <path
        d="M6 4L6.22108 4.59745C6.51097 5.38087 6.65592 5.77259 6.94167 6.05834C7.22741 6.34408 7.61913 6.48903 8.40255 6.77892L9 7L8.40255 7.22108C7.61913 7.51097 7.22741 7.65592 6.94166 7.94167C6.65592 8.22741 6.51097 8.61913 6.22108 9.40255L6 10L5.77892 9.40255C5.48903 8.61913 5.34408 8.22741 5.05833 7.94167C4.77259 7.65592 4.38087 7.51097 3.59745 7.22108L3 7L3.59745 6.77892C4.38087 6.48903 4.77259 6.34408 5.05833 6.05833C5.34408 5.77259 5.48903 5.38087 5.77892 4.59745L6 4Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const GreenPencilIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#4caf50"
      fill="none"
    >
      <path
        d="M6 22V13.6944C6 12.1287 6 11.3459 6.21454 10.6077C6.42908 9.86948 6.84589 9.21812 7.6795 7.91542L10.3359 3.76419C11.0885 2.58806 11.4648 2 12 2C12.5352 2 12.9115 2.58806 13.6641 3.76419L16.3205 7.91542C17.1541 9.21812 17.5709 9.86948 17.7855 10.6077C18 11.3459 18 12.1287 18 13.6944V22"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M7 11C7.63152 11.3231 8.4887 11.9732 9.28009 11.9991C10.2988 12.0324 10.9868 11.1372 12 11.1372C13.0132 11.1372 13.7012 12.0324 14.7199 11.9991C15.5113 11.9732 16.3685 11.3231 17 11"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M12 12L12 22"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M10 5H14"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const AiMagicIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      color="#000000"
      fill="none"
    >
      <path
        d="M10 7L9.48415 8.39405C8.80774 10.222 8.46953 11.136 7.80278 11.8028C7.13603 12.4695 6.22204 12.8077 4.39405 13.4842L3 14L4.39405 14.5158C6.22204 15.1923 7.13603 15.5305 7.80278 16.1972C8.46953 16.864 8.80774 17.778 9.48415 19.6059L10 21L10.5158 19.6059C11.1923 17.778 11.5305 16.864 12.1972 16.1972C12.864 15.5305 13.778 15.1923 15.6059 14.5158L17 14L15.6059 13.4842C13.778 12.8077 12.864 12.4695 12.1972 11.8028C11.5305 11.136 11.1923 10.222 10.5158 8.39405L10 7Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <path
        d="M18 3L17.7789 3.59745C17.489 4.38087 17.3441 4.77259 17.0583 5.05833C16.7726 5.34408 16.3809 5.48903 15.5975 5.77892L15 6L15.5975 6.22108C16.3809 6.51097 16.7726 6.65592 17.0583 6.94167C17.3441 7.22741 17.489 7.61913 17.7789 8.40255L18 9L18.2211 8.40255C18.511 7.61913 18.6559 7.22741 18.9417 6.94166C19.2274 6.65592 19.6191 6.51097 20.4025 6.22108L21 6L20.4025 5.77892C19.6191 5.48903 19.2274 5.34408 18.9417 5.05833C18.6559 4.77259 18.511 4.38087 18.2211 3.59745L18 3Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
    </svg>
  </SvgIcon>
);

export const LoadingIcon = ({ width, height }) => {
  return (
    <img
      src="/static/frontend/images/loading_icon.gif"
      alt="AI Magic Icon"
      width={width}
      height={height}
    />
  );
};

export const YellowSpiralIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      width="28"
      height="25"
      viewBox="0 0 28 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M28 10.6332L25.348 6.25106L19.3062 9.57929V0H14.0022V6.65643L5.30842 1.86465L2.65197 6.25106L8.69382 9.57929L0 14.3668L2.65197 18.7489L8.69382 15.425V25H14.0022V18.3478L22.6961 23.1353L25.348 18.7489L19.3062 15.425L28 10.6332Z"
        fill="#FFC249"
      />
    </svg>
  </SvgIcon>
);

export const TickIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 1.875C8.39303 1.875 6.82214 2.35152 5.486 3.24431C4.14985 4.1371 3.10844 5.40605 2.49348 6.8907C1.87852 8.37535 1.71762 10.009 2.03112 11.5851C2.34463 13.1612 3.11846 14.6089 4.25476 15.7452C5.39106 16.8815 6.8388 17.6554 8.4149 17.9689C9.99099 18.2824 11.6247 18.1215 13.1093 17.5065C14.594 16.8916 15.8629 15.8502 16.7557 14.514C17.6485 13.1779 18.125 11.607 18.125 10C18.1227 7.84581 17.266 5.78051 15.7427 4.25727C14.2195 2.73403 12.1542 1.87727 10 1.875ZM13.5672 8.56719L9.19219 12.9422C9.13415 13.0003 9.06522 13.0464 8.98934 13.0779C8.91347 13.1093 8.83214 13.1255 8.75 13.1255C8.66787 13.1255 8.58654 13.1093 8.51067 13.0779C8.43479 13.0464 8.36586 13.0003 8.30782 12.9422L6.43282 11.0672C6.31554 10.9499 6.24966 10.7909 6.24966 10.625C6.24966 10.4591 6.31554 10.3001 6.43282 10.1828C6.55009 10.0655 6.70915 9.99965 6.875 9.99965C7.04086 9.99965 7.19992 10.0655 7.31719 10.1828L8.75 11.6164L12.6828 7.68281C12.7409 7.62474 12.8098 7.57868 12.8857 7.54725C12.9616 7.51583 13.0429 7.49965 13.125 7.49965C13.2071 7.49965 13.2884 7.51583 13.3643 7.54725C13.4402 7.57868 13.5091 7.62474 13.5672 7.68281C13.6253 7.74088 13.6713 7.80982 13.7027 7.88569C13.7342 7.96156 13.7504 8.04288 13.7504 8.125C13.7504 8.20712 13.7342 8.28844 13.7027 8.36431C13.6713 8.44018 13.6253 8.50912 13.5672 8.56719Z"
        fill="#17B082"
      />
    </svg>
  </SvgIcon>
);

export const CrossIcon = (props) => (
  <SvgIcon {...props}>
    <svg
      width="17"
      height="18"
      viewBox="0 0 17 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.5 0.875C6.89303 0.875 5.32214 2.35152 3.986 3.24431C2.64985 4.1371 1.60844 5.40605 0.993482 6.8907C0.37852 8.37535 0.217618 10.009 0.531123 11.5851C0.844628 13.1612 1.61846 14.6089 2.75476 15.7452C3.89106 16.8815 5.3388 17.6554 6.9149 17.9689C8.49099 18.2824 10.1247 18.1215 11.6093 17.5065C13.094 16.8916 14.3629 15.8502 15.2557 14.514C16.1485 13.1779 16.625 11.607 16.625 10C16.6227 7.84581 15.766 5.78051 14.2427 4.25727C12.7195 2.73403 10.6542 1.87727 8.5 1.875ZM11.4422 11.0578C11.5003 11.1159 11.5463 11.1848 11.5777 11.2607C11.6092 11.3366 11.6254 11.4179 11.6254 11.5C11.6254 11.5821 11.6092 11.6634 11.5777 11.7393C11.5463 11.8152 11.5003 11.8841 11.4422 11.9422C11.3841 12.0003 11.3152 12.0463 11.2393 12.0777C11.1634 12.1092 11.0821 12.1253 11 12.1253C10.9179 12.1253 10.8366 12.1092 10.7607 12.0777C10.6848 12.0463 10.6159 12.0003 10.5578 11.9422L8.5 9.88359L6.44219 11.9422C6.38412 12.0003 6.31518 12.0463 6.23931 12.0777C6.16344 12.1092 6.08213 12.1253 6 12.1253C5.91788 12.1253 5.83656 12.1092 5.76069 12.0777C5.68482 12.0463 5.61588 12.0003 5.55782 11.9422C5.49975 11.8841 5.45368 11.8152 5.42226 11.7393C5.39083 11.6634 5.37466 11.5821 5.37466 11.5C5.37466 11.4179 5.39083 11.3366 5.42226 11.2607C5.45368 11.1848 5.49975 11.1159 5.55782 11.0578L7.61641 9L5.55782 6.94219C5.44054 6.82491 5.37466 6.66585 5.37466 6.5C5.37466 6.33415 5.44054 6.17509 5.55782 6.05781C5.67509 5.94054 5.83415 5.87465 6 5.87465C6.16586 5.87465 6.32492 5.94054 6.44219 6.05781L8.5 8.11641L10.5578 6.05781C10.6159 5.99974 10.6848 5.95368 10.7607 5.92225C10.8366 5.89083 10.9179 5.87465 11 5.87465C11.0821 5.87465 11.1634 5.89083 11.2393 5.92225C11.3152 5.95368 11.3841 5.99974 11.4422 6.05781C11.5003 6.11588 11.5463 6.18482 11.5777 6.26069C11.6092 6.33656 11.6254 6.41788 11.6254 6.5C11.6254 6.58212 11.6092 6.66344 11.5777 6.73931C11.5463 6.81518 11.5003 6.88412 11.4422 6.94219L9.3836 9L11.4422 11.0578Z"
        fill="#F53D3B"
      />
    </svg>
  </SvgIcon>
);
